/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ShopperProducts } from '@/scapi';
import { toImageUrl } from '@/lib/images/dynamic-image';
import type { AppConfig } from '@/types/config';

export interface ResolvedProductImage {
    link: string;
    alt?: string;
    title?: string;
}

export interface ResolvedProductImageDisplay {
    src: string | undefined;
    alt: string;
    title?: string;
}

export interface ProductResolvedImageFields {
    c_resolvedImage?: ResolvedProductImage;
}

type ProductWithResolvedImage = Partial<ShopperProducts.schemas['Product']> & {
    c_resolvedImage?: unknown;
};

/**
 * Reads and validates the response-only image added by the product SCAPI hook.
 */
export function getResolvedProductImage(
    product: ProductWithResolvedImage | undefined
): ResolvedProductImage | undefined {
    const value = product?.c_resolvedImage;
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const candidate = value as Record<string, unknown>;
    if (typeof candidate.link !== 'string' || candidate.link.length === 0) {
        return undefined;
    }

    return {
        link: candidate.link,
        ...(typeof candidate.alt === 'string' && candidate.alt.length > 0 ? { alt: candidate.alt } : {}),
        ...(typeof candidate.title === 'string' && candidate.title.length > 0 ? { title: candidate.title } : {}),
    };
}

/**
 * Converts the hook-resolved image into the display contract used by
 * non-component image resolvers such as checkout delivery groups.
 */
export function resolveProductImage(
    product: ProductWithResolvedImage | undefined,
    fallbackAlt: string,
    config?: AppConfig,
    width?: number
): ResolvedProductImageDisplay {
    const image = getResolvedProductImage(product);
    if (!image) {
        return { src: undefined, alt: fallbackAlt };
    }

    return {
        src: toImageUrl({ image: { link: image.link }, options: width ? { width } : undefined, config }) ?? image.link,
        alt: image.alt || fallbackAlt,
        title: image.title,
    };
}
